import { FormSectionHandler } from '../entity.handler';
import { DeviceBitmapConfiguration } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, EnumToFormBuilderComboValues, NumberField } from '../utils';
import { ICONS } from '../icons';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { DeviceParametersResponse, DeviceParametersUpdateRequest } from './parameters.section';

interface EditorValues {
    items?: ListFieldValue<DeviceBitmapConfiguration>[];
}

const IO_TYPES = ['digital_input', 'digital_output', 'can_input', 'value_input'];

export class DeviceBitmapsSectionHandler implements FormSectionHandler<DeviceParametersResponse, EditorValues> {

    type = 'form';
    icon = ICONS.devicebitmaps;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private applyUpdateCallback: (entity: any, payload: any) => Promise<DeviceParametersResponse>, 
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICE.IO_BITMAPS.${key}`, values);
    }

    applyUpdate(entity: DeviceParametersResponse, payload: DeviceParametersUpdateRequest): Promise<DeviceParametersResponse> {
        return this.applyUpdateCallback(entity, payload);
    }

    async mapEntityToEditor(entity: DeviceParametersResponse): Promise<EditorValues> {
        return {
            items: this.stateItemsToListItems(entity.parameters.bitmaps || []),
        };
    }

    async mapEditorToEntity(_entity: DeviceParametersUpdateRequest, values: EditorValues): Promise<DeviceParametersUpdateRequest> {
        return {
            parameters: { 
                bitmaps: values.items ? values.items.map(x => x.data) : undefined,
            }
        };
    }

    getFields(entity: DeviceParametersResponse): Promise<FormBuilderField[]> {
        return Promise.resolve().then(() => {
            return [
                {
                    id: 'items', title: this.translate('FIELDS.INPUTS'), type: 'list', required: false,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showFilterModal.bind(this), },
                        { id: 'edit', click: this.showFilterModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));

    }

    stateItemToListItem(item: DeviceBitmapConfiguration): ListFieldValue<DeviceBitmapConfiguration> {
        return {
            id: item.target_input,
            name: item.target_input,
            icon: ICONS.devicebitmaps,
            description: `(${item.source_input} >> ${(item.bitshift || 0).toString()}) & 0x${(item.bitmask || 1).toString(16)}`,
            data: item,
        };
    }

    stateItemsToListItems(items: DeviceBitmapConfiguration[]): ListFieldValue<DeviceBitmapConfiguration>[] {
        return (items || []).map(x => {
            return this.stateItemToListItem(x);
        });
    }

    updateOrAddItem(items: ListFieldValue<DeviceBitmapConfiguration>[], existingItem: ListFieldValue<DeviceBitmapConfiguration>, item: DeviceBitmapConfiguration) {
        const stateItems = items.map(x => x.data);
        if (existingItem) {
            const idx = stateItems.findIndex(x => x.target_input === existingItem.id);
            stateItems.splice(idx, 1);
        }
        stateItems.push(item || existingItem.data);
        return this.stateItemsToListItems(stateItems);
    }

    showFilterModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<DeviceBitmapConfiguration> {

        const model = (item && { ...item.data }) || { // pass some defaults for new items
            bitmask: 1,
            bitshift: 0,
        };

        model.bitmask = `0x${model.bitmask.toString(16)}`;        

        return this.admin.showFormModal<DeviceBitmapConfiguration>({
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [
                    { ...TextField('target_input', this.translate('FIELDS.TARGET_INPUT'), true, 1, 40), readonly: !!item },
                    { ...ComboField('target_type', this.translate('FIELDS.TARGET_TYPE'), true, EnumToFormBuilderComboValues(this.admin.i18n, 'ADMIN.EDITORS.IOTYPE.TYPES', IO_TYPES)), readonly: !!item },
                    TextField('source_input', this.translate('FIELDS.SOURCE_INPUT'), true, 1, 40),
                    { ...NumberField('bitshift', this.translate('FIELDS.SOURCE_BITSHIFT'), true, 0, 64, ''), description: this.translate('FIELDS.SOURCE_BITSHIFT_DESC') },
                    { ...TextField('bitmask', this.translate('FIELDS.SOURCE_BITMASK'), true, 1, 10), description: this.translate('FIELDS.SOURCE_BITMASK_DESC') },
                ],
            }],
        }, model).then(result => {
            if (result) {
                result.bitmask = parseInt(result.bitmask.toString(), 16);
                if (isNaN(result.bitmask)) {
                    result.bitmask = 1;
                }
                values.items = this.updateOrAddItem(values.items, item, result);
            }
            return result;
        });

    }

}
